
import { useAuth } from "@/contexts/auth";
import { useFavorites } from "@/contexts/FavoritesProvider";
import { useProviders } from "@/hooks/useProviders";
import { useProviderReviews } from "@/hooks/useProviderReviews";
import { useExploreMeta } from "@/hooks/useExploreMeta";
import { useFilteredProviders } from "@/components/explore/useFilteredProviders";
import ExploreFiltersHandler from "@/components/explore/ExploreFiltersHandler";
import ExploreContent from "@/components/explore/ExploreContent";
import { Provider, Territory } from "@/types/supabase";
import type { User } from "@supabase/supabase-js";

interface FiltersState {
  categories: string[];
  suburbs: string[];
  rating: string;
  verifiedOnly: boolean;
  showFavorites: boolean;
  topRatedOnly: boolean;
  featuredOnly: boolean;
}

const ExploreProvidersContainer = () => {
  const { user } = useAuth();
  const { favorites } = useFavorites();
  const { allCategories, territories } = useExploreMeta();
  
  // Fetch data from custom hooks
  const { data: providers = [], isLoading } = useProviders();
  const { data: allReviews = {} } = useProviderReviews();

  return (
    <ExploreFiltersHandler>
      {({
        searchQuery,
        setSearchQuery,
        activeFilters,
        setActiveFilters,
        appliedFilters,
        setAppliedFilters,
        handleSearch,
        handleResetFilters,
        sortOption,
        setSortOption,
      }) => (
        <ExploreContentWithFiltering 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          handleSearch={handleSearch}
          handleResetFilters={handleResetFilters}
          sortOption={sortOption}
          setSortOption={setSortOption}
          providers={providers}
          isLoading={isLoading}
          allCategories={allCategories}
          territories={territories}
          allReviews={allReviews}
          favorites={favorites}
          user={user}
        />
      )}
    </ExploreFiltersHandler>
  );
};

// Separate component to handle filtered providers with hooks
const ExploreContentWithFiltering = ({ 
  providers, 
  allReviews, 
  favorites, 
  user, 
  ...contentProps 
}: {
  providers: Provider[];
  allReviews: Record<string, Array<{ rating: number; is_public?: boolean }>>;
  favorites: string[];
  user: User | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeFilters: FiltersState;
  setActiveFilters: (filters: FiltersState) => void;
  appliedFilters: FiltersState;
  setAppliedFilters: (filters: FiltersState) => void;
  handleSearch: (e: React.FormEvent) => void;
  handleResetFilters: () => void;
  sortOption: string;
  setSortOption: (option: string) => void;
  allCategories: string[];
  territories: Territory[];
  isLoading: boolean;
}) => {
  // Process filtered providers using the hook - now safe to call in component
  const filteredProviders = useFilteredProviders({
    providers,
    searchQuery: contentProps.searchQuery,
    appliedFilters: contentProps.appliedFilters,
    sortOption: contentProps.sortOption,
    allReviews,
    favorites,
    userId: user?.id
  });

  return (
    <ExploreContent 
      {...contentProps}
      filteredProviders={filteredProviders}
      isLoading={contentProps.isLoading}
    />
  );
};

export default ExploreProvidersContainer;
